import clsx from 'clsx';
import { HTMLProps, ReactNode, useId } from 'react';

interface IFormCheckboxProps extends HTMLProps<HTMLInputElement> {
  inputClassName?: string;
  controlLabel?: ReactNode;
  isToggle?: boolean;
  labelClassName?: string;
  containerClassName?: string;
  disabled?: boolean;
}

const FormCheckBox: React.FC<IFormCheckboxProps> = ({
  name = '',
  onChange: handleChange,
  label,
  inputClassName = '',
  controlLabel = '',
  isToggle = false,
  value,
  checked,
  labelClassName = '',
  containerClassName = '',
  disabled = false,
  ...rest
}) => {
  // Stable per-instance id so the <label htmlFor> stays linked across re-renders
  // (uuidv4() regenerated on every render, breaking label clicks and a11y).
  const checkboxId = useId();

  return (
    <div className={clsx('w-full', containerClassName)}>
      {isToggle ? (
        <label htmlFor={checkboxId} className={clsx('flex items-center', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
          <div className='relative inline-block w-11 h-6'>
            <input
              value={value}
              id={checkboxId}
              name={name}
              className={clsx('sr-only', inputClassName)}
              type='checkbox'
              checked={checked}
              disabled={disabled}
              onChange={(e) => {
                handleChange && handleChange(e);
              }}
            />
            <div className={clsx('w-11 h-6 rounded-full transition-colors duration-200 ease-in-out', 
              disabled ? 'bg-gray-200' : checked ? 'bg-blue-600' : 'bg-gray-300')}></div>
            <div className={clsx('absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ease-in-out',
              checked ? 'translate-x-5' : 'translate-x-0',
              disabled ? 'opacity-50' : 'opacity-100')}></div>
          </div>
          <p className={clsx('ml-3 text-sm font-medium', disabled ? 'text-gray-400' : 'text-gray-900')}>{label}</p>
        </label>
      ) : (
        <div className='flex items-center gap-2'>
          <input
            value={value}
            id={checkboxId}
            name={name}
            className={clsx('checkbox-input', inputClassName)}
            type='checkbox'
            onChange={(e) => {
              handleChange && handleChange(e);
            }}
            checked={checked}
          />
          {label && (
            <label
              className={clsx('text-[16px] text-gray-700 hover:cursor-pointer', labelClassName)}
              htmlFor={checkboxId}
            >
              {label}
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default FormCheckBox;
